<template>
  <section id="requestForBooking">
    <h2 class="bg-white pt-12 text-center text-xl font-normal md:bg-beige1 md:pt-16 md:text-2xl">Request for booking</h2>
    <div class="bg-white pb-7 pt-2 md:bg-beige1 md:pb-14">
      <div class="mx-auto w-12 border-b border-gold1 md:w-14" />
    </div>
    <div class="bg-white pb-12 md:mx-[5%] md:py-20 e2:m-auto e2:max-w-6xl">
      <div class="flex flex-col gap-y-4 px-[16px] text-base md:gap-y-6 lg:px-[24.5%]">
        <div>
          <p class="text-base text-black2">Select first preferred date (JST)</p>
          <FormInputWrapper lang="en" :error="getErrorMessage('step1.selectedDate')" :required="true">
            <div class="bg-[#F4F1ED]">
              <OrganismsExperienceCalendar
                :key="props.calendarRefreshKey"
                :calendar-events="experienceEventDict"
                :display-month="displayMonth"
                :is-error="getErrorMessage('step1.selectedDate') !== ''"
                :is-loading="isLoading"
                :on-change-month="onChangeMonth"
                :on-click-date="onClickDate"
                :selected-date="form.step1.selectedDate ?? undefined"
              />
              <OrganismsCalendarCaption>
                <template v-if="hasCalendarNote" #note>
                  <p class="whitespace-pre-line break-words pb-2 text-sm text-black2" data-testid="calendar-note">{{ calendarNoteText }}</p>
                </template>
              </OrganismsCalendarCaption>
            </div>
          </FormInputWrapper>
          <div class="py-4 text-left text-sm text-black2">
            <p>
              If your preferred dates are not available, you can explore
              <a class="text-gold1 underline" href="#recommended-experiences" @click.prevent="scrollToRecommendedExperiences">recommended experiences</a>
              or
              <button class="text-gold1 underline" type="button" @click="handleOpenSearchByDate">search by date</button>.
            </p>
          </div>
          <WaitlistRegistrationCta v-if="props.isWaitlistEnabled" button-test-id="waitlist-registration-cta" :on-click="props.onClickGetNotified" />
        </div>
        <OrganismsPagesEnExperiencesDetailInfoBlock :enable-pc-layout="false" :experience="experience" />
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import OrganismsCalendarCaption from '@/components/molecules/wabunka/CalendarCaption/CalendarCaption.vue';
import OrganismsExperienceCalendar from '@/components/organisms/pages/en/experiences/detail/ExperienceCalendar/ExperienceCalendar.vue';
import { useCurrentTabIndex } from '@/composables/ui/useCurrentTabIndex';
import { useGlobalModal } from '@/composables/ui/useGlobalModal';
import { type WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { formatHyphenatedDate, getCurrentJstDayjs } from '@/utils/date';
import { FormInputWrapper } from '@/wabunka-src/common/components';
import { useGlobalWabunkaReservationForm } from '@/wabunka-src/common/features/Reservation/composables/forms/useGlobalWabunkaReservationForm';
import { sendGAWabunkaViewEventListEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/viewEventList';
import { SEARCH_TAB_INDEX } from '@/wabunka-src/constants/navigation';
import { useDisplayMonth } from '@/wabunka-src/features/Experience/composables/query/useDisplayMonth';
import { useListExperienceEventsByDate } from '@/wabunka-src/features/Experience/composables/query/useListExperienceEventsByDateQuery';
import { type WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';
import WaitlistRegistrationCta from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/pure/WaitlistRegistrationCta/WaitlistRegistrationCta.vue';
import { useCalendarNote } from '@/wabunka-src/features/Experience/pages/ExperienceDetail/composables/general/useCalendarNote';

import type dayjs from 'dayjs';

interface Props {
  /**
   * 他箇所で日付選択をした時に、カレンダーに反映するためのキー
   */
  calendarRefreshKey: number;
  onClickDate: (date: dayjs.Dayjs) => void;
  onClickGetNotified: () => void;
  /**
   * ウェイトリスト登録の露出制御。フラグ OFF の間は CTA を描画しない
   */
  isWaitlistEnabled: boolean;
  experienceId: string;
  experience: WabunkaExperienceViewModel;
}
const props = defineProps<Props>();
const experienceId = computed<string>(() => props.experienceId);
const { calendarNoteText, hasCalendarNote } = useCalendarNote(() => props.experience.calendarNote);
const { form, getErrorMessage } = useGlobalWabunkaReservationForm();
const now = getCurrentJstDayjs();
const startDateFrom = ref(formatHyphenatedDate(form.value.step1.selectedDate?.startOf('month') ?? now.startOf('month')));
const startDateTo = ref(formatHyphenatedDate(form.value.step1.selectedDate?.endOf('month') ?? now.endOf('month')));
const { experienceEventDict, isLoading } = useListExperienceEventsByDate(experienceId, { startDateFrom, startDateTo }, { accumulate: true });

const selectedDate = computed(() => form.value.step1.selectedDate);
const { displayMonth } = useDisplayMonth(experienceId, selectedDate);
const candidateEvents = ref<WabunkaExperienceEventViewModel[]>([]);
// これより先の予約導線から戻ってきた時、または日付が選択された時
watch(
  [() => form.value.step1.selectedDate, experienceEventDict],
  () => {
    if (form.value.step1.selectedDate == null) {
      return;
    }
    const date = formatHyphenatedDate(form.value.step1.selectedDate);
    candidateEvents.value = experienceEventDict.value[date] ?? [];
    // GAイベント送信
    sendGAWabunkaViewEventListEvent(props.experience, candidateEvents.value, date);
  },
  { immediate: true },
);
const onChangeMonth = (startDate: string, endDate: string) => {
  startDateFrom.value = startDate;
  startDateTo.value = endDate;
};
const { updateTabIndex } = useCurrentTabIndex('searchExperiencesModalTabIndex');
const { open: openSearchModal } = useGlobalModal('searchExperiencesModal');
const handleOpenSearchByDate = () => {
  updateTabIndex(SEARCH_TAB_INDEX.DATES);
  openSearchModal();
};

// スムーススクロールで推奨体験セクションに移動
const scrollToRecommendedExperiences = () => {
  document.querySelector('#recommended-experiences')?.scrollIntoView({
    behavior: 'smooth',
  });
};
</script>
